// LIGHT COPY of components/topology/TopologyNodes.jsx for /event-trace-light. The dark original is untouched;
// changes to it are NOT carried here automatically.
import React from 'react';
import { Handle, Position } from 'reactflow';
import { ROLE_COLORS } from '../../constants/topologyLight';

const riskBorder = {
  critical: '#EF4444',
  high:     '#F97316',
  normal:   null,
};

export function OTDeviceNode({ data }) {
  const { name, ip, role, roleClass, riskLevel = 'normal', isSelected, protocols = [], policyCount = 0, isInactive = false } = data;
  const roleColor = ROLE_COLORS[roleClass] || ROLE_COLORS[role] || '#475569';
  const border = isInactive ? '#CBD5E1' : (riskBorder[riskLevel] || roleColor);
  const pulseClass = riskLevel === 'critical' ? 'animate-pulse' : '';

  return (
    <div
      className={`relative rounded-lg px-3 py-2 cursor-pointer transition-all duration-150 ${isSelected ? 'ring-2 ring-cyan-400' : ''}`}
      style={{
        background: '#FFFFFF',
        border: `1.5px solid ${border}`,
        minWidth: 130,
        opacity: isInactive ? 0.25 : 1,
        boxShadow: (!isInactive && riskLevel === 'critical') ? `0 0 12px ${border}44` : '0 1px 4px rgba(15,23,42,0.12)',
      }}
    >
      {!isInactive && riskLevel !== 'normal' && (
        <div className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ${pulseClass}`}
          style={{ background: border }} />
      )}
      <Handle type="target" position={Position.Top} style={{ background: '#94A3B8', width: 6, height: 6 }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#94A3B8', width: 6, height: 6 }} />
      <div className="flex items-center gap-1.5 mb-1">
        <span className="px-1 py-0 rounded text-[10px] font-semibold" style={{ background: `${roleColor}22`, color: roleColor, border: `1px solid ${roleColor}44` }}>
          {role}
        </span>
        {policyCount > 0 && (
          <span className="px-1 py-0 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-400">
            P{policyCount}
          </span>
        )}
      </div>
      <div className="text-xs font-medium text-slate-800 leading-tight">{name}</div>
      <div className="text-[11px] text-slate-600 font-mono mt-0.5">{ip}</div>
      {protocols.length > 0 && (
        <div className="flex flex-wrap gap-0.5 mt-1">
          {protocols.slice(0, 3).map(p => (
            <span key={p} className="text-[9px] px-1 rounded" style={{ background: '#F1F5F9', color: '#475569' }}>
              {p.toUpperCase()}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function EdgeAgentNode({ data }) {
  const { hostname = 'X2mNative', policiesLoaded = 0, enforcementMode = 'dry-run', status = 'Online' } = data;
  const isOnline = status === 'Online' || status === 'Active';

  return (
    <div
      className="relative rounded-xl px-4 py-3 cursor-pointer"
      style={{
        background: '#ECFDF5',
        border: '1.5px solid #10B981',
        minWidth: 160,
        boxShadow: '0 0 16px rgba(16,185,129,0.2)',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: '#10B981', width: 7, height: 7 }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#10B981', width: 7, height: 7 }} />
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
        <span className="text-xs font-semibold text-emerald-700">Celerway Edge Agent</span>
      </div>
      <div className="text-xs font-medium text-slate-800">{hostname}</div>
      <div className="flex gap-1.5 mt-1.5">
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
          {policiesLoaded} policies
        </span>
      </div>
    </div>
  );
}

export function CloudNode({ data }) {
  const { label = 'EdgeNotary Cloud' } = data;
  return (
    <div
      className="rounded-2xl px-5 py-3 cursor-pointer"
      style={{
        background: '#F0F9FF',
        border: '1.5px solid #38BDF8',
        minWidth: 180,
        boxShadow: '0 0 20px rgba(56,189,248,0.15)',
      }}
    >
      <Handle type="source" position={Position.Bottom} style={{ background: '#38BDF8', width: 7, height: 7 }} />
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 text-sky-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
        <span className="text-sm font-semibold text-sky-700">{label}</span>
      </div>
      <div className="text-[11px] text-slate-600 mt-0.5 pl-6">Management Plane</div>
    </div>
  );
}

export function GOOSEBusNode({ data }) {
  const { hasActivity = false, rogueSource = null, policyBadge = null, width = 480 } = data;
  const isRogue = !!rogueSource;

  return (
    <div
      className="relative flex items-center justify-center rounded"
      style={{
        width,
        height: 28,
        background: isRogue
          ? 'linear-gradient(90deg, #FEE2E2, #FECACA, #FEE2E2)'
          : 'linear-gradient(90deg, #FEF9C3, #FDE68A, #FEF9C3)',
        border: `1.5px solid ${isRogue ? '#EF4444' : '#EAB308'}`,
        boxShadow: hasActivity
          ? `0 0 14px ${isRogue ? 'rgba(239,68,68,0.4)' : 'rgba(234,179,8,0.35)'}`
          : 'none',
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: isRogue ? '#EF4444' : '#EAB308', width: 6, height: 6 }} />
      <Handle type="source" position={Position.Right} style={{ background: isRogue ? '#EF4444' : '#EAB308', width: 6, height: 6 }} />

      <span className="text-[10px] font-bold tracking-widest" style={{ color: isRogue ? '#B91C1C' : '#92400E' }}>
        {isRogue ? 'GOOSE BUS — ROGUE INJECTION' : 'GOOSE PROTECTION DOMAIN BUS'}
      </span>

      {hasActivity && !isRogue && (
        <span className="absolute right-2 text-[9px] px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700 border border-yellow-400">
          {policyBadge || 'ACTIVE'}
        </span>
      )}

      {isRogue && (
        <span className="absolute right-2 text-[9px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-400 font-bold">
          MAC: {rogueSource.slice(-8)}
        </span>
      )}
    </div>
  );
}

export function ZoneLabelNode({ data }) {
  return (
    <div style={{
      fontSize: '9px', fontWeight: 700, letterSpacing: '0.13em',
      color: data.color || '#64748B', pointerEvents: 'none', userSelect: 'none',
    }}>
      {data.label}
    </div>
  );
}

export const TOPOLOGY_NODE_TYPES = {
  otDevice:  OTDeviceNode,
  edgeAgent: EdgeAgentNode,
  cloud:     CloudNode,
  gooseBus:  GOOSEBusNode,
  zoneLabel: ZoneLabelNode,
};
